import uFuzzy from '@leeoniya/ufuzzy';
import { useDeferredValue, useEffect, useMemo, useState } from 'react';

import { SongPreview } from '~/interfaces';
import { genreKey } from '~/modules/songs/hooks/use-genre-list';
import isSongRecentlyUpdated from '~/modules/songs/utils/is-song-recently-updated';
import clearString, { removeAccents } from '~/modules/utils/clear-string';
import { ExcludedLanguagesSetting, useSettingValue } from '~/routes/settings/settings-state';
import {
  DuetsFilter,
  isLengthMatch,
  LengthFilter,
  PlayedFilter,
} from '~/routes/sing-a-song/song-selection/components/filters/filter-options';
import type { FilterableSong } from '~/routes/sing-a-song/song-selection/components/filters/use-filter-categories';
import usePlayedSongIds from '~/routes/sing-a-song/song-selection/hooks/use-played-song-ids';
import { PersonalRecommendations, usePlaylists } from '~/routes/sing-a-song/song-selection/hooks/use-playlists';

type FilterFunc = (
  songList: SongPreview[],
  value: any,
  appliedFilters: AppliedFilters,
  list: SongPreview[],
) => SongPreview[];

export interface AppliedFilters {
  yearBefore?: number;
  yearAfter?: number;
  /**
   * Genre, Language and Length each hold however many picks the user made, and a song matching any
   * one of them is a match. A bare value is a set of one - the playlists set theirs that way, and
   * so did every filter before they could hold more than one.
   */
  language?: string | string[];
  excludeLanguages?: string[];
  skipExcludedLanguages?: boolean;
  search?: string;
  edition?: string;
  genre?: string | string[];
  recentlyUpdated?: boolean | null;
  duet?: boolean | null;
  specificSongs?: string[];
  additionalSongs?: string[];
  played?: PlayedFilter;
  length?: LengthFilter | LengthFilter[];
  /**
   * The ids of songs this machine has played, injected by `useSongListFilter` rather than chosen
   * on screen - the `played` filter needs them and cannot fetch them itself, the same way
   * `excludeLanguages` carries the setting it filters by.
   */
  playedSongIds?: string[];
}

/** The picks a multi-valued category holds. `''` is not one of them - it is how a category is cleared. */
export const filterValues = <T extends string>(value: T | T[] | undefined): T[] =>
  (value === undefined ? [] : Array.isArray(value) ? value : [value]).filter(Boolean) as T[];

/**
 * Whether a category is filtering anything. An empty pick list is the same as nothing set - the
 * filter icon's dot and the phone's count both ask this rather than whether the key exists.
 */
export const isFilterSet = (value: AppliedFilters[keyof AppliedFilters]) =>
  Array.isArray(value) ? value.length > 0 : value !== undefined;

/** The on-screen Duets category, which reads better as three named options than as a tri-state boolean. */
export const duetFilterToValue = (duets: DuetsFilter | undefined): boolean | null | undefined =>
  duets === 'duets' ? true : duets === 'solo' ? false : undefined;

const isSearchApplied = (appliedFilters: AppliedFilters) => clearString(appliedFilters?.search ?? '').length > 2;

const emptyFilters: AppliedFilters = {};

const ALPHANUMERIC__SPACE_REGEX = /[^0-9a-z ]/gi;

const getSearchString = (str: string) => removeAccents(str).toLowerCase().replace(ALPHANUMERIC__SPACE_REGEX, '');

/**
 * The fields beyond artist and title are optional because the phone gets a trimmed copy of the
 * song list - only what fits comfortably over the wire - so it searches the ones it has.
 */
type Searchable = Pick<SongPreview, 'artist' | 'title'> &
  Partial<Pick<SongPreview, 'genre' | 'edition' | 'year' | 'language' | 'series'>>;

/**
 * Everything a song is worth finding by.
 *
 * Artist and title used to be the only two, so typing a genre found nothing at all - no "anime",
 * no "christmas", no "eurovision", and no year. Genre in particular is the one people reach for:
 * 4,131 of the shipped songs carry one.
 *
 * The series a song comes from is curated separately and merged onto the song - nothing in the
 * catalogue records it (see `SongTag`) - so it is only searchable for the songs covered so far.
 */
const searchableParts = (song: Searchable): string[] => [
  song.artist,
  song.title,
  song.genre ?? '',
  song.edition ?? '',
  song.year ?? '',
  ...(song.language ?? []),
  ...(song.series ?? []),
];

/**
 * The searchable text of each song in a list, built once per list rather than once per keystroke.
 *
 * Normalising six thousand songs across seven fields costs about 30ms, and none of it depends on
 * what was typed - so on a set-top box every letter used to pay for work whose answer had not
 * changed. Keyed by the array itself and held weakly: the lists that reach here are memoised, so
 * identity holds for as long as the list does, and a new list simply gets a new entry.
 */
const haystacks = new WeakMap<object, string[]>();

const haystackFor = <T extends Searchable>(list: T[]): string[] => {
  const known = haystacks.get(list);
  if (known) return known;

  const built = list.map((song) => searchableParts(song).map(getSearchString).filter(Boolean).join(' '));
  haystacks.set(list, built);

  return built;
};

export function searchList<T extends Searchable>(list: T[], search: string): T[] {
  const cleanSearch = clearString(search);
  if (cleanSearch.length === 0) return list;

  const fuzz = new uFuzzy({});
  const [, info, order] = fuzz.search(haystackFor(list), getSearchString(search));

  if (order && info) {
    return order.map((item) => list[info.idx[item]]);
  }

  // Nothing the fuzzy matcher liked. Fall back to a plain contains, field by field rather than over
  // the fields joined together, so a match cannot straddle the end of one and the start of the next.
  return list.filter((song) => searchableParts(song).some((part) => clearString(part).includes(cleanSearch)));
}

export const filteringFunctions: Record<keyof AppliedFilters, FilterFunc> = {
  language: (songList, language: string | string[]) => {
    const wanted = filterValues(language);
    if (wanted.length === 0) return songList;

    return songList.filter((song) => {
      return wanted.some((language) => song.language.includes(language));
    });
  },
  excludeLanguages: (songList, languages: string[] = [], appliedFilters = {}) => {
    if (languages.length === 0 || isSearchApplied(appliedFilters) || appliedFilters.skipExcludedLanguages)
      return songList;

    return songList.filter((song) => {
      return !song.language.every((songLang) => languages.includes(songLang!));
    });
  },
  search: (songList, search: string) => {
    return searchList(songList, search);
  },
  duet: (songList, duet: boolean | null) => {
    if (duet === null) return songList;

    return songList.filter((song) => (duet ? song.tracksCount > 1 : song.tracksCount === 1));
  },
  yearBefore: (songList, yearBefore: number) => {
    if (!yearBefore) return songList;

    return songList.filter((song) => Number(song.year) < yearBefore);
  },
  yearAfter: (songList, yearAfter: number) => {
    if (!yearAfter) return songList;

    return songList.filter((song) => Number(song.year) >= yearAfter);
  },
  genre: (songList, genre: string | string[]) => {
    // Matched on the collapsed spelling, the same way the bar counts them, so picking K-Pop finds
    // the songs that call it Kpop too.
    const wanted = filterValues(genre).map(genreKey);
    if (wanted.length === 0) return songList;

    return songList.filter((song) => wanted.includes(genreKey(song.genre?.trim() ?? '')));
  },
  edition: (songList, edition: string) => {
    const cleanEdition = clearString(edition);

    return cleanEdition.length
      ? songList.filter((song) => clearString(song.edition ?? '').includes(cleanEdition))
      : songList;
  },
  recentlyUpdated: (songList) => {
    return songList.filter(isSongRecentlyUpdated);
  },
  specificSongs: (songList, specificSongs: string[], appliedFilters: AppliedFilters) => {
    if (isSearchApplied(appliedFilters)) return songList;

    return songList.filter((song) => specificSongs.includes(song.id));
  },
  additionalSongs: (songList, additionalSongIds: string[], _, list) => {
    const additionalSongs = list.filter((song) => additionalSongIds.includes(song.id));
    return [...songList, ...additionalSongs.filter((song) => !songList.includes(song))];
  },
  played: (songList, played: PlayedFilter, appliedFilters) => {
    const playedIds = new Set(appliedFilters.playedSongIds ?? []);

    return songList.filter((song) => (played === 'played' ? playedIds.has(song.id) : !playedIds.has(song.id)));
  },
  length: (songList, length: LengthFilter | LengthFilter[]) => {
    const buckets = filterValues(length);
    if (buckets.length === 0) return songList;

    return songList.filter((song) => buckets.some((bucket) => isLengthMatch(song, bucket)));
  },
  // Language exclusion is applied separately before playlist filtering (in useSongListFilter's prefilteredList),
  // so by the time playlist filters run, excluded songs are already removed. This entry exists to satisfy
  // the AppliedFilters type but intentionally does nothing here.
  skipExcludedLanguages: (songList) => songList,
  // Carried alongside `played` rather than filtering on its own - see AppliedFilters.
  playedSongIds: (songList) => songList,
};

const applyFilters = (list: SongPreview[], appliedFilters: AppliedFilters): SongPreview[] => {
  return (
    Object.entries(appliedFilters)
      // A filter set back to `undefined` is a filter that is off. Spreading one onto the object
      // leaves the KEY there with nothing behind it, and the function it names then runs against
      // nothing: `search` transliterates undefined and throws, `genre` collapses undefined into a
      // key and matches no song. Both are reachable by turning a filter off, so both are skipped
      // here rather than guarded one by one.
      .filter(([, value]) => value !== undefined)
      .filter((filters): filters is [keyof AppliedFilters, FilterFunc] => filters[0] in filteringFunctions)
      // Search goes first, so it reads the stable list whose searchable text is already built - run
      // after another filter it gets a fresh array, and rebuilds that text on every keystroke. The
      // rest intersect (or append, for additionalSongs), so their order gives the same answer.
      .sort(([a], [b]) => Number(b === 'search') - Number(a === 'search'))
      .reduce((songList, [name, value]) => filteringFunctions[name](songList, value, appliedFilters, list), list)
  );
};

/**
 * The filter categories applied to a list that carries the fields they read, rather than to a whole
 * `SongPreview[]`. The phone has its own song list - a trimmed copy of the same songs - and the
 * point of F15's "the same six categories natively" is that both sides answer with the same rules,
 * so the phone runs this rather than a second implementation of `filteringFunctions`.
 *
 * The cast is sound because nothing here builds a song: every filter narrows or reorders the list it
 * was handed, so the objects that come out are the ones that went in.
 */
export const applyFilterCategories = <T extends FilterableSong>(list: T[], appliedFilters: AppliedFilters): T[] =>
  applyFilters(list as unknown as SongPreview[], appliedFilters) as unknown as T[];

export const useSongListFilter = (
  list: SongPreview[],
  popular: string[],
  isLoading: boolean,
  additionalSong: string | null,
  personal?: PersonalRecommendations,
) => {
  const [excludedLanguages] = useSettingValue(ExcludedLanguagesSetting);
  const prefilteredList = useMemo(
    () => applyFilters(list, { excludeLanguages: excludedLanguages ?? [] }),
    [list, excludedLanguages],
  );

  const [selectedPlaylist, setSelectedPlaylist] = useState<string | null>(
    new URLSearchParams(global.location?.search).get('playlist') ?? null,
  );

  const playlists = usePlaylists(prefilteredList, popular, isLoading, personal);

  const setPlaylist = (name: string) => {
    /// push query param to url containing playlist name
    const url = new URL(global.location?.href);
    url.searchParams.set('playlist', name);
    global.history.replaceState(null, '', url.toString());

    setSelectedPlaylist(name);
  };

  const playlist = playlists.find((p) => p.name === selectedPlaylist) ?? playlists[0];
  const [filters, setFilters] = useState<AppliedFilters>(() => emptyFilters);

  // Auto-select the first playlist when none is set in the URL yet.
  useEffect(() => {
    if (selectedPlaylist === null && playlists.length > 0) {
      setPlaylist(playlists[0].name);
    }
  }, [selectedPlaylist, playlists]);

  // Only the search is dropped when the playlist changes. Everything else the filter panel holds -
  // a genre, a language, duets - is meant to survive it: the panel shows the playlist and those
  // filters side by side as things that are on at the same time, so switching one must not silently
  // clear the others out from under the list of active filters it is showing.
  useEffect(() => {
    setFilters((current) => (current.search ? { ...current, search: undefined } : current));
  }, [selectedPlaylist]);

  const playedSongIds = usePlayedSongIds(list);
  const deferredFilters = useDeferredValue(filters);

  // A category cleared on the panel leaves its key behind holding `undefined`, and spreading that
  // over the playlist's own filters below deletes them: picking a language, clearing it and then
  // switching to the English playlist left that playlist filtering by nothing at all. Only what
  // the panel actually holds is spread.
  const setFilterEntries = useMemo(
    () => Object.fromEntries(Object.entries(deferredFilters).filter(([, value]) => value !== undefined)),
    [deferredFilters],
  );

  const filteredList = useMemo(
    () =>
      applyFilters(list, {
        // While a search is active the user searches the whole catalog, regardless of which
        // playlist is selected. Only hidden songs are dropped (excluded via `list` upstream);
        // the excludeLanguages filter also steps aside on search (see filteringFunctions).
        ...(isSearchApplied(deferredFilters) ? {} : (playlist?.filters ?? {})),
        ...setFilterEntries,
        excludeLanguages: excludedLanguages ?? [],
        additionalSongs: additionalSong ? [additionalSong] : [],
        ...(deferredFilters.played ? { playedSongIds } : {}),
      }),
    [list, deferredFilters, setFilterEntries, excludedLanguages, playlist, additionalSong, playedSongIds],
  );

  return {
    filters,
    filteredList: filteredList,
    setFilters,
    selectedPlaylist,
    setSelectedPlaylist: setPlaylist,
    playlists,
    playlist,
    // Everything this screen can reach once hidden languages are dropped - what the filter panel
    // counts its options against, and the second number in "61 of 1 284 songs".
    catalogue: prefilteredList,
  };
};
